import type { CSSProperties } from "react";
import type { Guide } from "./useGuide.js";
import { BoutonInfos } from "../info/BoutonInfos.js";
import { useChaines } from "./useChaines.js";
import { FOND_SOUSTITRES, GuideSubtitles } from "./GuideSubtitles.js";
import { BoutonSousTitres } from "./BandesContenu.js";
import { SAUGE, contour, ellipse, plein, rond, titreItalien, verre } from "./guideStyles.js";
import { IconeBandes, IconeListe, IconeLecture, IconePause } from "./icones.js";

/**
 * ============================================================================
 *  GuideFloatingBar.tsx — La barre du mode main
 * ----------------------------------------------------------------------------
 *  Le visiteur a la caméra : les bandes sont rentrées, cette barre prend le relais,
 *  en bas, au style des autres panneaux (verre dépoli). Elle porte la lecture, la
 *  scène en cours avec sa progression, et le bouton qui rend la main au guide —
 *  « Reprendre le guide », qui devient « Continuer le guide » quand la scène
 *  explorée est finie (la voix s'est arrêtée avec elle : aucun changement de scène
 *  tout seul).
 *
 *  Dans l'exploration d'une scène depuis la fin de chapitre, il n'y a pas de voix :
 *  la barre n'a ni lecture ni progression, et son bouton est « Retour au chapitre ».
 * ==========================================================================*/

const HAUTEUR_BARRE = 64;
const MARGE_BAS = 24;
const MARGE_BAS_TELEPHONE = 12;

export function GuideFloatingBar({ guide, etroit }: { guide: Guide; etroit: boolean }) {
  const { t } = useChaines();
  const exploration = guide.mode === "exploration";
  const { scene, indexScene } = guide;
  // En exploration, la scène affichée est celle du store, pas celle de la voix.
  const nom = exploration ? guide.nomScene(guide.sceneId) : guide.nomScene(scene?.sceneId);
  const numero = exploration ? guide.segments.findIndex((s) => s.sceneId === guide.sceneId) + 1 : indexScene + 1;
  const progression = scene && scene.duree > 0 ? Math.max(0, Math.min(1, (guide.temps - scene.debut) / scene.duree)) : 0;

  const libelle = exploration
    ? t("back")
    : guide.finDeScene
      ? t(etroit ? "contShort" : "cont")
      : t(etroit ? "resumeShort" : "resume");
  const action = exploration ? guide.actions.retourChapitre : guide.actions.reprendre;

  const bas = etroit ? MARGE_BAS_TELEPHONE : MARGE_BAS;
  const position: CSSProperties = etroit
    ? { left: MARGE_BAS_TELEPHONE, right: MARGE_BAS_TELEPHONE, bottom: `calc(${bas}px + env(safe-area-inset-bottom))` }
    : { left: "50%", transform: "translateX(-50%)", width: "min(620px, calc(100% - 24px))", bottom: bas };

  return (
    <>
      {!exploration && guide.soustitres && (
        <div
          style={{
            position: "absolute",
            left: 12,
            right: 12,
            bottom: `calc(${bas + HAUTEUR_BARRE + 10}px + env(safe-area-inset-bottom))`,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 20,
          }}
        >
          <GuideSubtitles actif style={{ ...FOND_SOUSTITRES, fontSize: etroit ? 13.5 : 16, maxWidth: "min(760px, 100%)" }} />
        </div>
      )}

      <div
        role="group"
        aria-label={t("guideBar")}
        style={{
          ...verre,
          position: "absolute",
          boxSizing: "border-box",
          minHeight: HAUTEUR_BARRE,
          padding: etroit ? "8px 10px" : "10px 14px",
          display: "flex",
          alignItems: "center",
          gap: etroit ? 10 : 14,
          boxShadow: "0 10px 30px rgba(8,14,12,.35)",
          zIndex: 20,
          pointerEvents: "auto",
          ...position,
        }}
      >
        {!exploration && (
          <button
            type="button"
            aria-label={guide.lecture ? t("pause") : t("play")}
            style={rond(44)}
            onClick={guide.actions.basculerLecture}
          >
            {guide.lecture ? <IconePause taille={18} /> : <IconeLecture taille={18} />}
          </button>
        )}

        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ ...titreItalien(etroit ? 16 : 18), ...ellipse }}>
            {numero > 0 && <span style={{ color: SAUGE, fontSize: 12, marginRight: 8 }}>{numero}</span>}
            {nom}
          </span>
          {!exploration && (
            <span aria-hidden style={{ display: "block", height: 2, background: "rgba(246,238,227,.22)" }}>
              <span style={{ display: "block", height: "100%", width: `${progression * 100}%`, background: SAUGE }} />
            </span>
          )}
        </div>

        {!etroit && !exploration && <BoutonSousTitres guide={guide} />}

        <button type="button" style={{ ...plein, flex: "none", padding: etroit ? "0 12px" : "0 18px" }} onClick={action}>
          {!exploration && <IconeBandes taille={18} />}
          {libelle}
        </button>
      </div>
    </>
  );
}

/**
 * « Chapitres » en main : le miroir de « Quitter le guide », en haut à gauche, en verre
 * dépoli. Libellé « Chapitre 2 · Découvrir le domaine » sur ordinateur, « Chapitres » sur
 * téléphone, où il cède la place au message « Vous avez la main » le temps qu'il est là.
 */
export function BoutonChapitres({ guide, etroit }: { guide: Guide; etroit: boolean }) {
  const { t } = useChaines();
  if (etroit && guide.ui.annonce !== null) return null;
  const titre = guide.chapitre?.titre ?? "";
  return (
    <button
      type="button"
      className="gd-bouton"
      aria-haspopup="dialog"
      aria-label={t("openChapters")}
      style={{
        ...contour,
        ...verre,
        borderColor: "rgba(246,238,227,.4)",
        position: "absolute",
        top: `calc(${etroit ? 12 : 16}px + env(safe-area-inset-top))`,
        left: etroit ? 12 : 16,
        maxWidth: "min(46vw, 420px)",
        zIndex: 20,
        pointerEvents: "auto",
      }}
      onClick={(event) => guide.actions.ouvrirMenu(guide.mode === "main" || guide.mode === "exploration" ? "main" : "titre", event.currentTarget)}
    >
      <IconeListe taille={16} />
      <span style={ellipse}>
        {etroit ? t("chapters") : t("chapterButton", { n: guide.indexChapitre + 1, titre })}
      </span>
    </button>
  );
}

/** « Quitter » en main : les bandes — et leur bouton — sont rentrées. Sur téléphone, le message le remplace le temps qu'il est là. */
export function QuitterFlottant({ guide, etroit }: { guide: Guide; etroit: boolean }) {
  const { t } = useChaines();
  if (etroit && guide.ui.annonce !== null) return null;
  return (
    // Le menu d'informations accompagne « Quitter » : en main, les bandes — et leur bouton — sont rentrées
    // (handoff `design_handoff_credits`). Sur téléphone, c'est le ⋯.
    <div
      style={{
        position: "absolute",
        top: `calc(${etroit ? 12 : 16}px + env(safe-area-inset-top))`,
        right: etroit ? 12 : 16,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 8,
        pointerEvents: "none",
      }}
    >
      <BoutonInfos variante={etroit ? "plus" : "info"} habillage="verre" />
      <button
        type="button"
        style={{
          ...contour,
          ...verre,
          borderColor: "rgba(246,238,227,.4)",
          pointerEvents: "auto",
        }}
        onClick={() => guide.actions.quitter()}
      >
        {etroit ? t("quitShort") : t("quit")}
      </button>
    </div>
  );
}

/** « Vous avez la main. La voix continue. » — 3,5 s, en haut au centre, verre dépoli. */
export function AnnonceMain({ guide, etroit }: { guide: Guide; etroit: boolean }) {
  const { t } = useChaines();
  const texte = guide.ui.annonce === "main" ? t("toast") : guide.ui.annonce === "exploration" ? t("toastExplore") : "";
  const visible = guide.ui.annonce !== null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        ...verre,
        position: "absolute",
        top: `calc(${etroit ? 12 : 16}px + env(safe-area-inset-top))`,
        left: "50%",
        transform: "translateX(-50%)",
        padding: "10px 16px",
        fontSize: 13.5,
        fontWeight: 500,
        whiteSpace: "nowrap",
        maxWidth: "calc(100% - 24px)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        opacity: visible ? 1 : 0,
        transition: "opacity .3s ease",
        pointerEvents: "none",
        zIndex: 21,
      }}
    >
      {visible ? texte : ""}
    </div>
  );
}
